// Display helpers for the What-If page.
//
// The view model keeps the API's base units — minutes for effort, and an
// explicit source currency per value (participant costs are USD, process-level
// cost follows `metrics.currency`). Conversion happens only at render time,
// against the unit and currency the user picked.

import { convertCurrency, formatMoney } from "../../services/currencyService";

const MINUTES_PER_HOUR = 60;

export const TIME_UNITS = [
  { value: "minutes", label: "Minutes", short: "min" },
  { value: "hours", label: "Hours", short: "hr" },
];

const timeUnitConfig = (unit) =>
  TIME_UNITS.find((entry) => entry.value === unit) || TIME_UNITS[0];

const localize = (value, digits = 2) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  return parsed.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

// Stat cards read better with trailing zeros trimmed ("5 min", not "5.00 min"),
// unlike table cells where a fixed 2dp keeps columns aligned.
const compact = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  return parsed.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

export function convertMinutes(minutes, unit) {
  const parsed = Number(minutes);
  if (!Number.isFinite(parsed)) return null;
  return unit === "hours" ? parsed / MINUTES_PER_HOUR : parsed;
}

// `withUnit: false` yields a bare number for cells whose column header already
// names the unit.
export function formatDuration(minutes, unit, withUnit = true) {
  const value = localize(convertMinutes(minutes, unit));
  if (value == null) return "-";
  return withUnit ? `${value} ${timeUnitLabel(unit)}` : value;
}

export function timeUnitLabel(unit) {
  return timeUnitConfig(unit).label.toLowerCase();
}

export function timeUnitShort(unit) {
  return timeUnitConfig(unit).short;
}

// "27.5 min" — abbreviated unit, for stat cards.
export function formatDurationShort(minutes, unit) {
  const value = compact(convertMinutes(minutes, unit));
  return value == null ? "-" : `${value} ${timeUnitShort(unit)}`;
}

// "22.5 / 5 / 0 min" — several durations sharing one trailing unit.
export function formatDurationGroup(minutesList, unit) {
  const parts = minutesList.map(
    (minutes) => compact(convertMinutes(minutes, unit)) ?? "-",
  );
  return `${parts.join(" / ")} ${timeUnitShort(unit)}`;
}

// The same duration in whichever of hours/minutes `unit` is not — a quick
// approx hint beside a value for readers thinking in the other unit than the
// page's toggle happens to be set to.
export function formatDurationAlt(minutes, unit) {
  const other = unit === "hours" ? "minutes" : "hours";
  return formatDurationShort(minutes, other);
}

export function formatCount(value) {
  return compact(value) ?? "-";
}

export function formatAmount(amount, fromCurrency, toCurrency, rates) {
  const converted = convertCurrency(amount, fromCurrency, toCurrency, rates);
  return converted == null ? "-" : formatMoney(converted, toCurrency);
}

export function formatRate(ratePerHour, fromCurrency, toCurrency, rates, unit) {
  const perHour = convertCurrency(ratePerHour, fromCurrency, toCurrency, rates);
  if (perHour == null) return "-";
  const perUnit = unit === "hours" ? perHour : perHour / MINUTES_PER_HOUR;
  return `${formatMoney(perUnit, toCurrency)}/${timeUnitShort(unit)}`;
}

export function formatPercent(value, digits = 1) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "-";
  return `${localize(parsed, digits)}%`;
}
